import type {
  DashboardMetric,
  DashboardMetricCategory,
  DashboardMetricsData,
  MetricBranchValue,
  MetricTrendPoint,
  RawDashboardMetricRow,
  RawDashboardMetricsPayload,
} from "./types";

/** Canonical key for matching a MetricCode across the SP's result sets. Set 1
 *  sends "AdmissionCNT" while sets 2 & 3 send "AdmissionCnt"; matching on the
 *  raw string would drop every trend/branch series, so key on trim+lowercase. */
const codeKey = (code: unknown): string => String(code ?? "").trim().toLowerCase();

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const higherIsBetterOf = (v: unknown): boolean | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1 ? true : v === 0 ? false : null;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["1", "true", "t", "y", "yes"].includes(s)) return true;
    if (["0", "false", "f", "n", "no"].includes(s)) return false;
  }
  return null;
};

const truthy = (v: unknown): boolean => higherIsBetterOf(v) === true;

/** Groups the SP's raw three result sets into per-category metric cards, sorted
 *  by MetricSequence, with each metric's per-branch values attached (matched
 *  case-insensitively on MetricCode). Mirrors the backend formatter, so the UI
 *  behaves the same whether the endpoint returns raw or formatted data. */
function fromRaw(payload: RawDashboardMetricsPayload): DashboardMetricCategory[] {
  // code → trend series (chronological). Matched case-insensitively: set 1 sends
  // "AdmissionCNT" while the trend set sends "AdmissionCnt".
  const trendByCode = new Map<string, MetricTrendPoint[]>();
  for (const row of payload.MetricTrend ?? []) {
    if (!row || typeof row !== "object") continue;
    const key = codeKey(row.MetricCode);
    if (!key) continue;
    const point: MetricTrendPoint = {
      startDate: row.StartOfPeriod ?? "",
      endDate: row.EndofPeriod ?? "",
      value: num(row.MetricValue),
      summaryCode: row.SummaryCode ?? "",
    };
    if (!trendByCode.has(key)) trendByCode.set(key, []);
    trendByCode.get(key)!.push(point);
  }
  for (const points of trendByCode.values()) {
    points.sort((a, b) => a.startDate.localeCompare(b.startDate));
  }

  // code → per-branch values.
  const branchByCode = new Map<string, MetricBranchValue[]>();
  for (const row of payload.MetricBranch ?? []) {
    if (!row || typeof row !== "object") continue;
    const key = codeKey(row.MetricCode);
    const branchSystemID = row.BranchSystemID ?? "";
    if (!key || !branchSystemID) continue;
    const value: MetricBranchValue = {
      branchSystemID,
      branchCode: row.branch_code ?? "",
      branchName: row.branch_name ?? "",
      value: num(row.MetricValue),
      priorValue: num(row.PrevMetricValue),
    };
    if (!branchByCode.has(key)) branchByCode.set(key, []);
    branchByCode.get(key)!.push(value);
  }

  const order: string[] = [];
  const byCategory = new Map<string, DashboardMetric[]>();

  for (const row of (payload.DashBoardMetrics ?? []) as RawDashboardMetricRow[]) {
    if (!row || typeof row !== "object") continue;
    const code = String(row.MetricCode ?? "").trim();
    if (!code) continue;
    const category = String(row.MetricCategory ?? "Uncategorized");
    if (!byCategory.has(category)) {
      byCategory.set(category, []);
      order.push(category);
    }
    byCategory.get(category)!.push({
      code,
      name: row.MetricShortDesc ?? code,
      category,
      serviceLineSystemID: "",
      formula: row.MetricFormula ?? "",
      benchmarkLabel:
        row.BenchMarkLabel != null && String(row.BenchMarkLabel) !== ""
          ? String(row.BenchMarkLabel)
          : null,
      calc: row.MetricCalc ?? "",
      currentValue: num(row.Curr_MetricValue),
      priorValue: num(row.Prev_MetricValue),
      higherIsBetter: higherIsBetterOf(row.TargetHigherIsBetter),
      isKeyMetric: truthy(row.IsKeyMetric),
      isCurrentState: truthy(row.IsCurrentState),
      sequence: num(row.MetricSequence) ?? Number.MAX_SAFE_INTEGER,
      trend: trendByCode.get(codeKey(code)) ?? [],
      branches: branchByCode.get(codeKey(code)) ?? [],
    });
  }

  return order.map((category) => ({
    category,
    metrics: byCategory.get(category)!.sort((a, b) => a.sequence - b.sequence),
  }));
}

/** Normalizes whatever the endpoint returned into categories. Accepts both the
 *  formatted response (`categories`) and the SP's raw payload
 *  (`DashBoardMetrics`/`MetricTrend`/`MetricBranch`). */
export function toMetricCategories(
  data: DashboardMetricsData | null | undefined,
): DashboardMetricCategory[] {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray((data as { categories?: unknown }).categories)) {
    return (data as { categories: DashboardMetricCategory[] }).categories;
  }
  if (Array.isArray((data as RawDashboardMetricsPayload).DashBoardMetrics)) {
    return fromRaw(data as RawDashboardMetricsPayload);
  }
  return [];
}
